import 'moment';

import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import moment from 'moment';
import { fromEvent, interval, Observable, of, Subscription } from 'rxjs';
import { delay, map, take } from 'rxjs/operators';

@Component({
  selector: 'app-toast',
  imports: [AsyncPipe],
  template: `
    <div
      #toast
      class="toast custom-toast {{ alertLevel }}"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div class="toast-header">
        {{ heading }}

        <div class="flex-end">
          <small>
            {{ createdDateTimeDescription$ | async }}
          </small>
          <button
            class="close"
            type="button"
            aria-label="Close"
            (click)="onDismiss()"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      </div>
      <div class="toast-body">
        {{ body }}
      </div>
    </div>
  `,
})
export class ToastComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('toast') toastElt: ElementRef<HTMLDivElement>;

  @Input() alertLevel: 'success' | 'info' | 'warn' | 'danger';
  @Input() heading: string;
  @Input() body: string;
  @Input() timeout = -1;
  @Input() animate = true;
  @Input() animationSlideDirection: 'up' | 'down' | 'right' | 'left';

  @Output() dismiss = new EventEmitter();

  createdDateTimeDescription$: Observable<string>;

  private createdDateTime: moment.Moment;
  private dismissed = false;
  private timeoutSubscription: Subscription;
  private animationEnd$: Observable<AnimationEvent>;
  private get enterAnimationClass() {
    return `slide-in-${this.animationSlideDirection}`;
  }

  private get exitAnimationClass() {
    return `slide-out-${this.animationSlideDirection}`;
  }

  ngOnInit() {
    this.createdDateTime = moment();
    this.createdDateTimeDescription$ = interval(1000).pipe(
      map(() => this.createdDateTime.fromNow()),
    );

    if (this.timeout > 0) {
      this.timeoutSubscription = of(null)
        .pipe(delay(this.timeout), take(1))
        .subscribe(() => {
          if (!this.dismissed) {
            this.onDismiss();
          }
        });
    }
  }

  ngAfterViewInit(): void {
    const divElt = this.toastElt.nativeElement;

    this.animationEnd$ = fromEvent<AnimationEvent>(divElt, 'animationend');
    if (this.animate) {
      this.animationEnd$.pipe(take(1)).subscribe(() => {
        divElt.classList.remove(this.enterAnimationClass);
      });

      divElt.classList.add(this.enterAnimationClass);
    }
  }

  onDismiss() {
    this.dismissed = true;
    if (this.animate) {
      this.animationEnd$.pipe(take(1)).subscribe(() => {
        this.dismiss.emit();
      });

      this.toastElt.nativeElement.classList.add(this.exitAnimationClass);
    } else {
      this.dismiss.emit();
    }
  }

  ngOnDestroy() {
    if (this.timeoutSubscription) {
      this.timeoutSubscription.unsubscribe();
    }
  }
}
